import {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from 'react';
import { SuggestionProps } from '@tiptap/suggestion';

import { spacing32 } from 'approot/shared/styles/spacing.style';
import * as Styled from './richtext-hashtag-dropdown.style';
import {
  ARROW_UP,
  ARROW_DOWN,
  ENTER,
} from 'approot/shared/ally/keyboard.hooks';

type Props = Omit<SuggestionProps, 'items'> & {
  items: { name: string; link: string }[];
};

const RichtextHashtagDropdown = forwardRef((props: Props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  const filteredItems = props.items.filter(i =>
    i.name
      .toLowerCase()
      .replaceAll(' ', '')
      .startsWith(props.query.toLowerCase())
  );
  const items = filteredItems.slice(0, 5);
  const position = props.clientRect && props.clientRect();

  const selectItem = useCallback(
    (index: number) => {
      const item = items[index];

      if (item) {
        props.command(item);
      }
    },
    [items, props]
  );

  const upHandler = useCallback(() => {
    if (selectedIndex === 0) return;

    setSelectedIndex(selectedIndex - 1);
  }, [selectedIndex]);

  const downHandler = useCallback(() => {
    if (selectedIndex === items.length - 1) return;

    setSelectedIndex(selectedIndex + 1);
  }, [selectedIndex, items]);

  const enterHandler = useCallback(() => {
    selectItem(selectedIndex);
  }, [selectedIndex, selectItem]);

  useEffect(() => setSelectedIndex(0), [props.items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === ARROW_UP) {
        upHandler();
        return true;
      }

      if (event.key === ARROW_DOWN) {
        downHandler();
        return true;
      }

      if (event.key === ENTER) {
        enterHandler();
        return true;
      }

      return false;
    },
  }));

  return (
    <Styled.RichtextHashtagDropdown
      style={{ top: position?.top ? position.top + spacing32 : 0 }}
    >
      {items.length ? (
        items.map((item, index) => (
          <Styled.RichtextHashtagDropdownItem
            key={index}
            onClick={() => selectItem(index)}
            $isActive={selectedIndex === index}
          >
            {item.name}
          </Styled.RichtextHashtagDropdownItem>
        ))
      ) : (
        <Styled.RichtextHashtagDropdownNoResult>
          No result
        </Styled.RichtextHashtagDropdownNoResult>
      )}
    </Styled.RichtextHashtagDropdown>
  );
});

export default RichtextHashtagDropdown;
